import { useMemo } from 'react'
import { useMsal } from '@azure/msal-react'
import { useGraphData } from '../useGraphData'

/**
 * Centralized hook to get user email with MSAL fallback
 *
 * This hook ensures user email is always available by:
 * 1. First trying to get from graphData (Microsoft Graph API)
 * 2. Falling back to MSAL account token claims if graphData is not loaded yet
 *
 * @returns An object containing:
 *   - userEmail: The user's email address (string | undefined)
 *   - isReady: Whether the email is ready to use (boolean)
 *   - isLoading: Whether we're still loading the email (boolean)
 */
export const useUserEmail = () => {
  const [graphData, graphDataLoading] = useGraphData()
  const { instance } = useMsal()

  // Get user email with fallback to MSAL account
  const userEmail = useMemo(() => {
    // First try to get from graphData
    if (graphData?.mail) {
      return graphData.mail
    }
    // Fallback to MSAL account if graphData is not loaded yet
    const activeAccount = instance.getActiveAccount()
    if (activeAccount) {
      return (activeAccount.idTokenClaims?.preferred_username ?? activeAccount.idTokenClaims?.email) as string | undefined
    }
    return undefined
  }, [graphData?.mail, instance])

  const isReady = !!userEmail && !graphDataLoading

  return {
    userEmail,
    isReady,
    isLoading: graphDataLoading
  }
}
